import "react-quill/dist/quill.snow.css";
import ReactQuill, { Quill } from "react-quill";
// ---- Quill Modules ----
import ResizeModule from "@ssumo/quill-resize-module";
import React, { useRef, useMemo, useEffect, useCallback } from "react";

// ---- MUI ----
import Paper from "@mui/material/Paper";
import { Theme, SxProps } from "@mui/material/styles";

// Register the resize module with Quill
Quill.register("modules/resize", ResizeModule);


const SizeStyle = Quill.import('attributors/style/size');
Quill.register(SizeStyle, true);

// Change default block tag to DIV for better compatibility with email signatures (P tags add margins)
const Block = Quill.import('blots/block');
Block.tagName = 'DIV';
Quill.register(Block, true);

// ---- Custom Image Blot ----
// This allows us to handle image attributes like alt text and styles
const BlockEmbed = Quill.import('blots/block/embed');
const ATTRIBUTES = ['height', 'width', 'align', 'src', 'alt', 'style'];
class ImageBlot extends BlockEmbed {
  static create(value: string) {
    const node = super.create(value);
    if (typeof value === 'string') {
      node.setAttribute('src', value);
    }

    return node;
  }

  static formats(domNode: HTMLElement) {
    return ATTRIBUTES.reduce((formats: Record<string, string | null>, attribute: string) => {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {} as Record<string, string | null>);
  }

  static value(domNode: HTMLElement) {
    return domNode.getAttribute('src');
  }

  format(name: string, value: string) {
    if (ATTRIBUTES.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}
ImageBlot.blotName = 'image';
ImageBlot.tagName = 'IMG';

Quill.register('blots/custom-image', ImageBlot, true);

/**
 * Props for the EmailSignatureEditor component.
 */
export interface EmailSignatureEditorProps {
  /** Initial HTML to populate the editor with */
  html: string;
  /** Callback fired whenever the editor HTML changes */
  onChange: (html: string) => void;
  /** Optional additional styling for the root Paper component */
  sx?: SxProps<Theme>;
}

const EmailSignatureEditor: React.FC<EmailSignatureEditorProps> = ({
  html,
  onChange,
  sx,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);

  /**
   * Custom handler to insert an image as a base64 data‑URL
   */
  const handleImageParse = useCallback((): void => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = () => {
      if (!input.files || input.files.length === 0) return;
      const file = input.files[0];
      const reader = new FileReader();

      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        const position = range ? range.index : quill.getLength();
        quill.insertEmbed(position, "image", reader.result as string, "user");
        quill.setSelection({ index: position + 1, length: 0 });
      };

      reader.readAsDataURL(file);
    };
  }, []);

  /**
   * Quill modules configuration
   */
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ["bold", "italic", "underline"],
        [{ align: [] }],
        ["link", "image"],
      ],
      handlers: {
        image: handleImageParse,
      },
    },
    resize: {
      showToolbar: false,
      showSize: true,
    },
    clipboard: {
      matchVisual: true,
    },
    keyboard: {
      bindings: {
        'delete image': {
          key: 'Backspace',
          handler (range: { index: number; length: number }) {
            // const [blot] = this.quill.getLeaf(range.index);

            // if (blot instanceof Quill.import('blots/custom-image')) {

            //   this.quill.deleteText(range.index, 1, 'user');
            //   return false; // Prevent default behavior
            // }

            // IMPORTANT: leave this here; fixes the issue where pressing backspace on an image doesn't delete it, i have no idea why this works
            return true; // Allow default behavior
          },
        },
      },
    }
  }), [handleImageParse]);

  /**
   * Allowed Quill formats
   */
  const formats = useMemo(
    () => [
      "bold",
      "italic",
      "underline",
      "align",
      "link",
      "image",
      "color",
      "size", // Include size for text formatting,
      "background",
      "font",
      "width", // For image resizing
      "height", // For image resizing
      "style"
    ],
    []
  );

  /**
   * Relay HTML changes upstream
   */
  const handleChange = useCallback(
    (value: string) => {
      onChange(value);
    },
    [onChange]
  );

  useEffect(() => {
    // Ensure the editor is initialized with the correct HTML
    if (quillRef.current) {
      quillRef.current.getEditor().clipboard.dangerouslyPasteHTML(html);
    }
  }, [html]);

  return (
    <Paper
      variant="outlined"
      sx={{
        minHeight: 200,
        p: 1.5,
        "& .ql-container": {
          minHeight: 200,
          fontFamily: "inherit",
        },
        "& .ql-editor": {
          minHeight: 200
        },
        ...sx,
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder="Compose your email signature…"
        style={{ minHeight: 200, borderRadius: 4 }}
        defaultValue={html}
      />
    </Paper>
  );
};

export default EmailSignatureEditor;